import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import { ArrowUpRight, MapPin } from "lucide-react";

const CHAPTERS = [
  { n: "01", t: "Design with intent", d: "Every space begins with a meticulous plan. We listen to your vision and translate it into architecture that lasts." },
  { n: "02", t: "Build with mastery", d: "Noble materials and expert hands. We work with the finest craftsmen to achieve flawless, durable finishes." },
  { n: "03", t: "Transform with trust", d: "Transparent communication, clean job sites and a written 1-year workmanship warranty on everything we deliver." },
];

export default function AboutPage() {
  const [about, setAbout] = useState("https://images.pexels.com/photos/18285887/pexels-photo-18285887.jpeg");
  useEffect(() => { api.get("/site-settings").then((r) => r.data.about_image && setAbout(r.data.about_image)).catch(() => {}); }, []);
  return (
    <div>
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-20">
        <Reveal>
          <Overline>About Us</Overline>
          <h1 className="mt-6 font-serif font-light text-5xl md:text-7xl leading-[0.95] max-w-4xl">
            Craftsmanship you can trust, <em className="gold-gradient-text">right here in Virginia.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-white/60 font-body text-lg leading-relaxed">
            GI Remodeling LLC is a full-service home remodeling company serving homeowners throughout Virginia. We combine luxury design with honest, dependable workmanship — so your remodel feels effortless from first sketch to final walkthrough.
          </p>
        </Reveal>
      </section>

      <section className="border-y border-white/10 bg-[#080808]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <img src={about} alt="Our work" className="w-full aspect-[4/3] object-cover border border-white/10" />
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>Our Story</Overline>
            <h2 className="mt-6 font-serif font-light text-3xl md:text-4xl leading-tight">Building relationships, not just remodels.</h2>
            <p className="mt-6 text-white/60 font-body leading-relaxed">
              For over 15 years, families across Virginia have trusted us to transform the heart of their homes. We treat every project as if it were our own — with respect for your time, your budget and your peace of mind.
            </p>
            <p className="mt-4 text-white/60 font-body leading-relaxed">
              As a licensed and insured company, we hold ourselves to the highest standard of accountability. And because we’re confident in our work, we back it with a written <span className="text-gold">1-year workmanship warranty</span>.
            </p>
            <p className="mt-6 inline-flex items-center gap-2 text-white/50 text-sm uppercase tracking-widest">
              <MapPin size={15} className="text-gold" /> Proudly serving all of Virginia
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36">
        <Reveal><Overline>How We Work</Overline><h2 className="mt-6 font-serif font-light text-4xl md:text-5xl">Design. Build. Transform.</h2></Reveal>
        <div className="mt-16 grid gap-px bg-white/10 border border-white/10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1}>
              <div className="group bg-[#0a0a0a] p-8 md:p-12 grid md:grid-cols-[120px_1fr] gap-6 md:gap-12 items-start hover:bg-[#0f0f0f] transition-colors duration-500">
                <span className="font-serif text-5xl md:text-7xl text-gold/30 group-hover:text-gold/70 transition-colors duration-500">{c.n}</span>
                <div>
                  <h3 className="font-serif text-2xl md:text-3xl">{c.t}</h3>
                  <p className="mt-4 text-white/60 font-body max-w-2xl leading-relaxed">{c.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-4xl md:text-5xl">Let’s build something you’ll love.</h2>
          <Link data-testid="about-cta" to="/contact" className="mt-8 inline-flex items-center gap-3 bg-gold text-black text-sm font-semibold uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors duration-300">
            Request a Free Quote <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
